import { MAX_RUN_MS, failureAdvice } from '@config/queueConfig'
import { fmtDuration, fmtTime } from './queueFormat'
import { WarningIcon } from './queueIcons'

/**
 * Screen 8B — the failure state, deliberately the fullest treatment of any state
 * ("failed is the one I care about the most, because if a report dies at 10
 * minutes the person needs to know whether to retry or change a filter").
 *
 * This keeps a short "What it got through" TIMELINE, which now stacks BELOW the
 * drawer's run summary rather than replacing it: for a run that died the sequence
 * genuinely is the story, but the reader still needs when it ran and what
 * triggered it, and this panel used to be shown INSTEAD of all of that. Keep it
 * as the diagnosis and leave the facts to `JobRunSummary` — do not unify them.
 *
 * Copy rule: where we know the real remedy we say it; where we don't we say the
 * run stopped and offer retry, rather than inventing advice. Report 22 must
 * never be told to narrow its date range (CC-50022 proved that makes it worse).
 *
 * ## No buttons (spec 111)
 *
 * This panel used to carry `onRetryWithFilters` / `onRetryAsIs` and render
 * "Change filters & retry" + "Try again as-is". Both moved to the drawer's fixed
 * footer, which is where Himaad's Section 3 puts every state's actions, and the
 * props were REMOVED rather than left accepted-and-ignored — a prop that looks
 * wired and does nothing is the shape this codebase keeps getting caught by.
 *
 * The footer is also the only place the retry can be gated correctly: re-running
 * from the drawer is a silent no-op for the 21 specialized reports, and this
 * panel does not know the report's kind. This panel is now purely the diagnosis.
 */
const JobFailedPanel = ({ job }) => {
  if (!job) return null
  const hitCap = job.durationMs != null && job.durationMs >= MAX_RUN_MS * 0.95
  const advice = failureAdvice(job.reportId)
  const capMinutes = Math.round(MAX_RUN_MS / 60000)

  return (
    <div>
      <div className="mb-3.5 flex gap-2.5 rounded-xl border border-red-200 bg-red-50 px-3.5 py-3 text-[12.5px] text-red-700">
        <WarningIcon className="mt-px w-4 h-4 shrink-0" />
        <div>
          <b>
            {hitCap
              ? `It ran for ${fmtDuration(job.durationMs)} and stopped at the ${capMinutes}-minute limit.`
              : `It ran for ${fmtDuration(job.durationMs)} and stopped without returning data.`}
          </b>
          {advice && <div className="mt-1 leading-relaxed">{advice} That&apos;s a known issue with how this report&apos;s data is built, and it&apos;s being worked on.</div>}
          {!advice && job.error && <div className="mt-1 leading-relaxed">{job.error}</div>}
        </div>
      </div>

      <div className="mb-2.5 text-[10.5px] font-semibold uppercase tracking-[0.7px] text-gray-500">What it got through</div>
      <div className="mb-3.5 rounded-xl border border-gray-100 bg-gray-50 px-3.5 py-3">
        <ol className="relative space-y-3 pl-6 before:absolute before:bottom-2 before:left-[7px] before:top-1.5 before:w-0.5 before:bg-gray-200">
          <li className="relative">
            <span className="absolute -left-[23px] top-1 h-3 w-3 rounded-full border-[2.5px] border-cb-green bg-cb-green" aria-hidden="true" />
            <div className="text-[12.5px] font-semibold text-cb-navy">Queued</div>
            <div className="mt-px text-[11.5px] text-gray-500">{fmtTime(job.createdAt)} · by you</div>
          </li>
          <li className="relative">
            <span className="absolute -left-[23px] top-1 h-3 w-3 rounded-full border-[2.5px] border-cb-green bg-cb-green" aria-hidden="true" />
            <div className="text-[12.5px] font-semibold text-cb-navy">Started on the server</div>
            <div className="mt-px text-[11.5px] text-gray-500">{fmtTime(job.createdAt)}</div>
          </li>
          <li className="relative">
            <span className="absolute -left-[23px] top-1 h-3 w-3 rounded-full border-[2.5px] border-gray-300 bg-white" aria-hidden="true" />
            <div className="text-[12.5px] font-semibold text-red-700">
              {hitCap ? `Stopped at the ${capMinutes}-minute limit` : 'Stopped without returning data'}
            </div>
            <div className="mt-px text-[11.5px] text-gray-500">
              {fmtTime(job.finishedAt ?? job.updatedAt)} · no rows returned
              {job.percent === 0 ? ' · progress never passed 0%' : ` · reached ${job.percent}%`}
            </div>
          </li>
        </ol>
      </div>

    </div>
  )
}

export default JobFailedPanel
